// Login (P0.6): e-mail + senha com confirmação, recuperação de senha e Google.
import type { SupabaseClient } from '@supabase/supabase-js';
import { BackendError } from './repository';

export interface SessionUser {
  id: string;
  email: string;
  name: string;
}

export interface Session {
  user: SessionUser;
}

export type AuthErrorCode = 'invalid_credentials' | 'email_not_confirmed' | 'email_taken' | 'weak_password' | 'network' | 'unknown';

export class AuthError extends Error {
  constructor(public code: AuthErrorCode) {
    super(code);
    this.name = 'AuthError';
  }
}

export interface SignUpData {
  name: string;
  email: string;
  password: string;
}

export interface AuthService {
  getSession(): Promise<Session | null>;
  onChange(cb: (session: Session | null) => void): () => void;
  /** Cria a conta; o Supabase envia o e-mail de confirmação. */
  signUp(data: SignUpData): Promise<{ needsConfirmation: boolean }>;
  signIn(email: string, password: string): Promise<Session>;
  signOut(): Promise<void>;
  /** Pede o link de recuperação. Mensagem neutra: não revela se o e-mail existe. */
  resetPassword(email: string): Promise<void>;
  updatePassword(password: string): Promise<void>;
  /** Abriu o link de recuperação: o app mostra "Nova senha" até a senha ser trocada (AC-092). */
  inPasswordRecovery(): boolean;
  onPasswordRecovery(cb: (active: boolean) => void): () => void;
  /** Google ou Microsoft. Web: redireciona. Desktop: abre o navegador do sistema (PKCE + beedoit://) — ver platform/oauth.ts. */
  signInWithOAuth(provider: OAuthProvider): Promise<void>;
}

/** Provedores de login social: "azure" é o nome do provedor Microsoft no Supabase (AC-089). */
export type OAuthProvider = 'google' | 'azure';

/** A Microsoft só devolve o e-mail se ele for pedido no escopo. */
export const OAUTH_SCOPES: Partial<Record<OAuthProvider, string>> = { azure: 'email' };

export const AUTH_CALLBACK_URL = 'beedoit://auth/callback';

/** Marca "veio do link de recuperação" — fica ligada até a senha nova ser salva ou a sessão sair. */
export function createRecoveryFlag() {
  let active = false;
  const listeners = new Set<(active: boolean) => void>();
  return {
    get: () => active,
    set(next: boolean) {
      if (next === active) return;
      active = next;
      listeners.forEach((cb) => cb(next));
    },
    on(cb: (active: boolean) => void) {
      listeners.add(cb);
      return () => void listeners.delete(cb);
    },
  };
}

function toSession(user: { id: string; email?: string | null; user_metadata?: Record<string, unknown> } | null | undefined): Session | null {
  if (!user) return null;
  return {
    user: {
      id: user.id,
      email: user.email ?? '',
      name: typeof user.user_metadata?.name === 'string' ? (user.user_metadata.name as string) : '',
    },
  };
}

function mapAuthError(error: { message?: string; status?: number; code?: string } | null): AuthError {
  const msg = `${error?.code ?? ''} ${error?.message ?? ''}`.toLowerCase();
  if (msg.includes('invalid login') || msg.includes('invalid_credentials')) return new AuthError('invalid_credentials');
  if (msg.includes('not confirmed') || msg.includes('email_not_confirmed')) return new AuthError('email_not_confirmed');
  if (msg.includes('already registered') || msg.includes('user_already_exists')) return new AuthError('email_taken');
  if (msg.includes('password')) return new AuthError('weak_password');
  if (msg.includes('fetch') || msg.includes('network')) return new AuthError('network');
  return new AuthError('unknown');
}

export interface SupabaseAuthOptions {
  /** Desktop (Tauri): Google ou Microsoft pelo navegador do sistema com retorno em beedoit:// */
  oauthDesktop?: (provider: OAuthProvider) => Promise<void>;
  /** Login próprio pelo token de identidade (US-048); false quando o provedor não tem ID de cliente no app. */
  oauthIdToken?: (provider: OAuthProvider) => Promise<boolean>;
  /** Para onde levam os links dos e-mails de confirmação e recuperação: o endereço do app na web, beedoit:// no desktop (AC-091). */
  siteUrl?: string;
}

export function createSupabaseAuth(client: SupabaseClient, opts: SupabaseAuthOptions = {}): AuthService {
  // o evento pode chegar antes da tela existir (no desktop o link abre o app), por isso a marca é guardada aqui
  const recovery = createRecoveryFlag();
  client.auth.onAuthStateChange((event) => {
    if (event === 'PASSWORD_RECOVERY') recovery.set(true);
    else if (event === 'SIGNED_OUT') recovery.set(false);
  });
  return {
    async getSession() {
      const { data } = await client.auth.getSession();
      return toSession(data.session?.user);
    },
    onChange(cb) {
      const { data } = client.auth.onAuthStateChange((_event, session) => cb(toSession(session?.user)));
      return () => data.subscription.unsubscribe();
    },
    async signUp({ name, email, password }) {
      const { data, error } = await client.auth.signUp({
        email,
        password,
        options: { data: { name }, emailRedirectTo: opts.siteUrl },
      });
      if (error) throw mapAuthError(error);
      return { needsConfirmation: !data.session };
    },
    async signIn(email, password) {
      const { data, error } = await client.auth.signInWithPassword({ email, password });
      if (error) throw mapAuthError(error);
      const s = toSession(data.user);
      if (!s) throw new AuthError('unknown');
      return s;
    },
    async signOut() {
      await client.auth.signOut();
    },
    async resetPassword(email) {
      const { error } = await client.auth.resetPasswordForEmail(email, { redirectTo: opts.siteUrl });
      if (error && mapAuthError(error).code === 'network') throw new AuthError('network');
    },
    async updatePassword(password) {
      const { error } = await client.auth.updateUser({ password });
      if (error) throw mapAuthError(error);
      recovery.set(false);
    },
    inPasswordRecovery: recovery.get,
    onPasswordRecovery: recovery.on,
    async signInWithOAuth(provider) {
      if (opts.oauthIdToken && (await opts.oauthIdToken(provider))) return;
      if (opts.oauthDesktop) return opts.oauthDesktop(provider);
      const { error } = await client.auth.signInWithOAuth({
        provider,
        options: { redirectTo: opts.siteUrl ?? window.location.origin, scopes: OAUTH_SCOPES[provider] },
      });
      if (error) throw new BackendError('unknown', error.message);
    },
  };
}
